import moment from "moment";
import { forEach, filter, values as objectValues } from "lodash";
import { getOrderPrice } from "./order";
import { getBusinessDate } from "./date";

const DAYS = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];

// moment().day(): 0=Domingo..6=Sábado. Lo pasamos a Lunes=0..Domingo=6
// para que la grilla empiece en Lunes, como se pidió.
function toMondayFirstIndex(momentDay) {
  return (momentDay + 6) % 7;
}

// Horario real del restaurante (8am a 2am, cruza medianoche). Fijo a
// propósito en vez de calcularlo de los datos: mientras se prueba la app
// aparecen pedidos a cualquier hora, y mostrar esas horas "de prueba" no
// tiene sentido para el negocio y además ensancha la grilla innecesariamente.
const OPEN_HOUR = 8;
const CLOSE_HOUR = 2;

function buildOperatingHours(openHour, closeHour) {
  const hours = [];
  let hour = openHour;
  while (true) {
    hours.push(hour);
    if (hour === closeHour) break;
    hour = (hour + 1) % 24;
  }
  return hours;
}

export const OPERATING_HOURS = buildOperatingHours(OPEN_HOUR, CLOSE_HOUR);

// Agrupa las ventas (S/) por día de la semana × hora del día.
// Usa moment.parseZone (no moment() a secas) para leer la hora tal como
// viene en el timestamp (offset de Lima), sin que la convierta a la hora
// local del navegador/dispositivo que esté mirando el reporte.
export function buildSalesHeatmap(orders) {
  // Los pedidos de una mesa liberada como pérdida (nunca se cobraron) no
  // cuentan como venta real, mismo criterio que en Ventas por Producto.
  const validOrders = filter(
    orders,
    (order) =>
      order.created_at &&
      order.product_data &&
      order.payment_type !== "LOSS" &&
      order.payment_status !== "VOID"
  );
  if (!validOrders.length) return null;

  const cells = {};

  forEach(validOrders, (order) => {
    const date = moment.parseZone(order.created_at);
    const hour = date.hour();
    if (!OPERATING_HOURS.includes(hour)) return;

    // Fila = día operativo: la 1 AM del sábado va en la fila del viernes.
    const dayIndex = toMondayFirstIndex(getBusinessDate(order.created_at).day());
    const revenue = getOrderPrice(order);
    const key = `${dayIndex}-${hour}`;
    cells[key] = (cells[key] || 0) + revenue;
  });

  const grid = DAYS.map((label, dayIndex) => ({
    label,
    values: OPERATING_HOURS.map((hour) => cells[`${dayIndex}-${hour}`] || 0),
  }));

  const maxValue = Math.max(0, ...objectValues(cells));

  return { hours: OPERATING_HOURS, grid, maxValue };
}

const MONTHS = [
  "Ene",
  "Feb",
  "Mar",
  "Abr",
  "May",
  "Jun",
  "Jul",
  "Ago",
  "Sep",
  "Oct",
  "Nov",
  "Dic",
];

// Igual que buildSalesHeatmap, pero agrupa por día de la semana × mes del
// año (Ene..Dic) en vez de por hora — para ver qué meses del año tienen
// más movimiento, sumando todos los años que caigan en el rango de fechas
// elegido (no separa año por año, mismo criterio simple que ya usa el
// resto de reportes de sumar todo el rango sin desglosar por período).
export function buildMonthlyHeatmap(orders) {
  const validOrders = filter(
    orders,
    (order) =>
      order.created_at &&
      order.product_data &&
      order.payment_type !== "LOSS" &&
      order.payment_status !== "VOID"
  );
  if (!validOrders.length) return null;

  const cells = {};

  forEach(validOrders, (order) => {
    const date = getBusinessDate(order.created_at);
    const month = date.month();
    const dayIndex = toMondayFirstIndex(date.day());
    const revenue = getOrderPrice(order);
    const key = `${dayIndex}-${month}`;
    cells[key] = (cells[key] || 0) + revenue;
  });

  const grid = DAYS.map((label, dayIndex) => ({
    label,
    values: MONTHS.map((_, month) => cells[`${dayIndex}-${month}`] || 0),
  }));

  const maxValue = Math.max(0, ...objectValues(cells));

  return { months: MONTHS, grid, maxValue };
}

export function formatHourLabel(hour) {
  const period = hour < 12 ? "AM" : "PM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour} ${period}`;
}

const COLOR_STOPS = [
  [255, 247, 236],
  [253, 212, 158],
  [252, 141, 89],
  [215, 48, 31],
  [127, 0, 0],
];

export function getHeatColor(value, maxValue) {
  if (!maxValue) return `rgb(${COLOR_STOPS[0].join(",")})`;

  const ratio = Math.min(1, Math.max(0, value / maxValue));
  const segment = ratio * (COLOR_STOPS.length - 1);
  const index = Math.min(COLOR_STOPS.length - 2, Math.floor(segment));
  const localRatio = segment - index;

  const [r1, g1, b1] = COLOR_STOPS[index];
  const [r2, g2, b2] = COLOR_STOPS[index + 1];

  const r = Math.round(r1 + (r2 - r1) * localRatio);
  const g = Math.round(g1 + (g2 - g1) * localRatio);
  const b = Math.round(b1 + (b2 - b1) * localRatio);

  return `rgb(${r}, ${g}, ${b})`;
}

export function isDarkCell(value, maxValue) {
  if (!maxValue) return false;
  return value / maxValue > 0.55;
}

// Escala de grises equivalente a getHeatColor, para impresoras sin color
// (@media print and (monochrome)) — sin esto, una impresora blanco y negro
// puede convertir el degradé amarillo→rojo a grises muy parecidos entre sí
// y las celdas dejan de diferenciarse.
const GRAY_STOPS = [246, 200, 150, 90, 20];

export function getHeatGrayColor(value, maxValue) {
  if (!maxValue) return `rgb(${GRAY_STOPS[0]}, ${GRAY_STOPS[0]}, ${GRAY_STOPS[0]})`;

  const ratio = Math.min(1, Math.max(0, value / maxValue));
  const segment = ratio * (GRAY_STOPS.length - 1);
  const index = Math.min(GRAY_STOPS.length - 2, Math.floor(segment));
  const localRatio = segment - index;

  const g1 = GRAY_STOPS[index];
  const g2 = GRAY_STOPS[index + 1];
  const g = Math.round(g1 + (g2 - g1) * localRatio);

  return `rgb(${g}, ${g}, ${g})`;
}
